import React, { useEffect, useState } from 'react';
import './ChessTimer.css';
import CardSearch from '../pages/CardSearch'; // adjust path if needed
import CalculatorModal from '../components/CalculatorModal';


const ChessTimer = ({ setPaused, paused, setHasStarted, setClockRunning }) => {
  const [initialTime, setInitialTime] = useState(1320); // 22 min default
  const [player1Time, setPlayer1Time] = useState(initialTime);
  const [player2Time, setPlayer2Time] = useState(initialTime);
  const [activePlayer, setActivePlayer] = useState(null);
  const [sideTimerActive, setSideTimerActive] = useState(false);
  const [sideTime, setSideTime] = useState(180);
  const [timerStarted, setTimerStarted] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [previousPlayer, setPreviousPlayer] = useState(null);
  const [blockedPlayer, setBlockedPlayer] = useState(null);
  const [lastGiver, setLastGiver] = useState(null);
  const [lastSwitchTimestamp, setLastSwitchTimestamp] = useState(null);
  const [showCardSearch, setShowCardSearch] = useState(false);
  const [showInitialConfig, setShowInitialConfig] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [player1LP, setPlayer1LP] = useState(8000);
  const [player2LP, setPlayer2LP] = useState(8000);
  const [player1Input, setPlayer1Input] = useState("");
  const [player2Input, setPlayer2Input] = useState("");
  const [player1Operator, setPlayer1Operator] = useState("+");
  const [player2Operator, setPlayer2Operator] = useState("+");

  const toggleOperator = (player) => {
    if (player === 1) {
      setPlayer1Operator((prev) => (prev === "+" ? "-" : "+"));
    } else {
      setPlayer2Operator((prev) => (prev === "+" ? "-" : "+"));
    }
  };

  const updateLP = (player) => {
    if (player === 1) {
      const amount = parseInt(player1Input) || 0;
      setPlayer1LP((prev) =>
        player1Operator === "+" ? prev + amount : Math.max(0, prev - amount)
      );
      setPlayer1Input("");
    } else {
      const amount = parseInt(player2Input) || 0;
      setPlayer2LP((prev) =>
        player2Operator === "+" ? prev + amount : Math.max(0, prev - amount)
      );
      setPlayer2Input("");
    }
  };




  useEffect(() => {
    if (showCardSearch) {
      document.body.classList.add('card-search-open');
    } else {
      document.body.classList.remove('card-search-open');
    }
  }, [showCardSearch]);

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem('chessTimerState'));
    if (saved && saved.timerStarted) {
      setInitialTime(saved.initialTime);
      setPlayer1Time(saved.player1Time);
      setPlayer2Time(saved.player2Time);
      setActivePlayer(saved.activePlayer);
      setPaused(saved.paused);
      setTimerStarted(saved.timerStarted);
      setLastSwitchTimestamp(saved.lastSwitchTimestamp);
      setPreviousPlayer(saved.previousPlayer);
      setClockRunning(saved.timerStarted);
    } else {
      setShowInitialConfig(true); // no timer running → show config on reset
    }
  }, []);

  useEffect(() => {
    window.setCardSearchOpen?.(showCardSearch);
  }, [showCardSearch]);


  useEffect(() => {
    const state = {
      initialTime,
      player1Time,
      player2Time,
      activePlayer,
      paused,
      timerStarted,
      lastSwitchTimestamp,
      previousPlayer,
    };
    localStorage.setItem('chessTimerState', JSON.stringify(state));
  }, [initialTime, player1Time, player2Time, activePlayer, paused, timerStarted, lastSwitchTimestamp, previousPlayer]);

  useEffect(() => {
    if (!timerStarted) return;

    const interval = setInterval(() => {
      if (!paused && !sideTimerActive) {
        if (activePlayer === 1 && player1Time > 0) {
          setPlayer1Time((t) => t - 1);
        } else if (activePlayer === 2 && player2Time > 0) {
          setPlayer2Time((t) => t - 1);
        }
      }

      if (sideTimerActive && sideTime > 0) {
        setSideTime((t) => t - 1);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [paused, activePlayer, player1Time, player2Time, sideTimerActive, sideTime, timerStarted]);

  useEffect(() => {
    if (blockedPlayer && lastSwitchTimestamp) {
      const timer = setTimeout(() => {
        setBlockedPlayer(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [blockedPlayer, lastSwitchTimestamp]);

  useEffect(() => {
    if (!timerStarted && showInitialConfig) {
      window.setShowGlobalTopButtons?.(true);
    } else {
      window.setShowGlobalTopButtons?.(false);
    }
  }, [timerStarted, showInitialConfig]);

  useEffect(() => {
    if (!timerStarted && showInitialConfig) {
      window.setShowGlobalMuteButton?.(true);
    } else {
      window.setShowGlobalMuteButton?.(false);
    }
  }, [timerStarted, showInitialConfig]);



  const format = (s) => {
    const rounded = Math.round(s);
    const minutes = Math.floor(rounded / 60);
    const seconds = String(rounded % 60).padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  const handleTap = (player) => {
    const now = Date.now();
    if (!timerStarted || sideTimerActive) return;
    // If paused, resume timer when tapping
    if (paused) {
      setPaused(false);
    }

    if (blockedPlayer === player && now - lastSwitchTimestamp < 2000) return;

    if (!activePlayer) {
      const next = player === 1 ? 2 : 1;
      setActivePlayer(next);
      setPreviousPlayer(player);
      setLastSwitchTimestamp(now);
      return;
    }

    if (activePlayer === player) {
      const nextPlayer = player === 1 ? 2 : 1;

      if (
        previousPlayer === nextPlayer &&
        lastSwitchTimestamp &&
        now - lastSwitchTimestamp < 1000
      ) {
        if (player === 1) setPlayer1Time((t) => t + 0.8);
        else setPlayer2Time((t) => t + 0.8);
        setBlockedPlayer(nextPlayer);
      }

      setActivePlayer(nextPlayer);
      setPreviousPlayer(player);
      setLastSwitchTimestamp(now);
    }
  };

  const confirmReset = () => {
    setPlayer1Time(initialTime);
    setPlayer2Time(initialTime);
    setActivePlayer(null);
    setPaused(false);
    setTimerStarted(false);
    setClockRunning(false);
    setShowResetConfirm(false);
    setShowInitialConfig(true); // show time selection
    localStorage.removeItem('chessTimerState');
  };

  const startSideTimer = () => {
    setPaused(true);
    setSideTime(180);
    setSideTimerActive(true);
  };

  const resumeFromSideTimer = () => {
    setSideTimerActive(false);
    setPaused(true);
  };

  return (
    <div className="chess-clock-ui">
      {!timerStarted && showInitialConfig ? (
        <div className="timer-config">
          <h3>Select Initial Time</h3>
          <div className="time-options">
            {[300, 600, 900, 1320].map((sec) => (
              <button
                key={sec}
                className={`timer-select-btn ${initialTime === sec ? 'selected' : ''}`}
                onClick={() => {
                  setInitialTime(sec);
                  setPlayer1Time(sec);
                  setPlayer2Time(sec);
                  setShowInitialConfig(false);
                  setTimerStarted(false);
                  setClockRunning(false);
                  setHasStarted(false);
                }}
              >
                {sec / 60} min
              </button>
            ))}
          </div>
          <button
            className="timer-start-btn"
            onClick={() => {
              setHasStarted(true);
              setTimerStarted(true);
              setClockRunning(true);
            }}
          >
            Start Duel
          </button>
        </div>
      ) : (
        <>
          {/* TOP PLAYER - flipped entire block */}
          <div
            className={`chess-clock ${activePlayer === 1 ? 'active' : 'inactive'} flipped`}
            onClick={() => handleTap(1)}
          >
            <div className="chess-time">{format(player1Time)}</div>

            {/* <div className="lp-panel flipped" onClick={(e) => e.stopPropagation()}>
              <div>LP: {player1LP}</div>
              <input
                type="number"
                value={player1Input}
                onChange={(e) => setPlayer1Input(e.target.value)}
              />
              <button className="toggle-btn" onClick={() => toggleOperator(1)}>{player1Operator}</button>
              <button onClick={() => updateLP(1)}>Update LP</button>
            </div> */}
          </div>

          <div className="chess-controls">
            <button onClick={() => setPaused(!paused)}>
              {paused ? '▶ Play' : 'Pause'}
            </button>
            <button onClick={() => setShowResetConfirm(true)}>Reset</button>
            <button onClick={startSideTimer}>Side Timer</button>
            <button onClick={() => setShowCalculator(true)}>
              Open Calculator
            </button>
            <button onClick={() => setShowCardSearch(true)}>Search Card</button>
          </div>

          {sideTimerActive && (
            <div className="side-timer-modal">
              <div className="side-timer-content">
                <h2>Side Timer</h2>
                <p>{format(sideTime)}</p>
                <button onClick={resumeFromSideTimer}>Resume Duel</button>
              </div>
            </div>
          )}

          {showResetConfirm && (
            <div className="chess-modal-overlay">
              <div className="chess-modal">
                <h2>Reset?</h2>
                <div className="chess-modal-buttons">
                  <button className="chess-confirm" onClick={confirmReset}>Confirm</button>
                  <button className="chess-cancel" onClick={() => setShowResetConfirm(false)}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* BOTTOM PLAYER - normal */}
          <div
            className={`chess-clock ${activePlayer === 2 ? 'active' : 'inactive'}`}
            onClick={() => handleTap(2)}
          >
            <div className="chess-time">{format(player2Time)}</div>

            {/* <div className="lp-panel" onClick={(e) => e.stopPropagation()}>
              <div>LP: {player2LP}</div>
              <input
                type="number"
                value={player2Input}
                onChange={(e) => setPlayer2Input(e.target.value)}
              />
              <button onClick={() => toggleOperator(2)}>{player2Operator}</button>
              <button onClick={() => updateLP(2)}>Update LP</button>
            </div> */}
          </div>
        </>
      )}

      {showCardSearch && (
        <div className="card-search-modal-overlay" onClick={() => setShowCardSearch(false)}>
          <div className="card-search-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-card-search" onClick={() => setShowCardSearch(false)}>✕</button>
            <CardSearch />
          </div>
        </div>
      )}

      <CalculatorModal show={showCalculator} onClose={() => setShowCalculator(false)} />
    </div>
  );


};

export default ChessTimer;
